import { getPatchCanvasOffsetY } from './patchAssets';

const CANVAS_CONFIG = {
  SCALE_FACTOR: 2,
  BOARDING_PASS_WIDTH: 370,
  BOARDING_PASS_HEIGHT: 552,
  PADDING: 16,
  WIDTH: 370 + 16 * 2,
  HEIGHT: 552 + 16 * 2,
  BORDER_RADIUS: 16,
  CUTOUT_Y_RATIO: 0.65,
  CUTOUT_RADIUS: 20,
  BORDER_WIDTH: 1,
  PATCH_SIZE: 180,
  MARGINS: {
    MAIN: 16,
    ACCENT_SPACING: 0
  },
  LOGOS: {
    MAIN: {
      CONTAINER: {
        WIDTH: 246,
        HEIGHT: 38,
        X_POSITION: 16,
        Y_POSITION: 14
      },
      IMAGE: {
        HEIGHT: 15,
        PADDING: {
          HORIZONTAL: 16,
          VERTICAL: 11
        }
      }
    },
    SPACEFORCE_ICON: {
      CONTAINER: {
        WIDTH: 38,
        HEIGHT: 38,
        X_POSITION: 270,
        Y_POSITION: 14
      },
      IMAGE: {
        WIDTH: 14,
        HEIGHT: 20,
        PADDING: {
          HORIZONTAL: 12,
          VERTICAL: 9
        }
      }
    },
    LEVEL_62_ICON: {
      CONTAINER: {
        WIDTH: 38,
        HEIGHT: 38,
        X_POSITION: 316,
        Y_POSITION: 14
      },
      IMAGE: {
        WIDTH: 15,
        HEIGHT: 12,
        PADDING: {
          HORIZONTAL: 12,
          VERTICAL: 13
        }
      }
    },
    BACKGROUND_ACCENT_SPACE: {
      WIDTH: 351.5,
      HEIGHT: 351.5,
      X_POSITION: 9.25,
      Y_POSITION: 57,
      OPACITY: 1
    },
    BACKGROUND_ACCENT_EARTH: {
      WIDTH: 205,
      HEIGHT: 275,
      X_POSITION: 0,
      Y_POSITION: -20,
      OPACITY: 1
    },
    SPACEFORCE_ACCENT: {
      WIDTH: 211,
      HEIGHT: 316,
      X_POSITION: 0,
      Y_POSITION: 236,
      OPACITY: 0.8
    }
  }
};

export const drawTextWithLetterSpacing = (ctx, text, x, y, letterSpacing) => {
  if (!letterSpacing || letterSpacing === 0) {
    ctx.fillText(text, x, y);
    return;
  }

  const chars = text.split('');
  let currentX = x;

  chars.forEach((char) => {
    ctx.fillText(char, currentX, y);
    const charWidth = ctx.measureText(char).width;
    currentX += charWidth + letterSpacing;
  });
};

export const getFontFamilyForWeight = (weight) => {
  if (weight <= 300) return 'TTFirsNeueLight';
  if (weight <= 500) return 'TTFirsNeueNormal';
  if (weight <= 700) return 'TTFirsNeueMedium';
  return 'TTFirsNeueMedium';
};

export const drawGradientBorder = (
  ctx,
  x,
  y,
  width,
  height,
  borderRadius,
  borderWidth = 1,
  backgroundColor = 'transparent'
) => {
  ctx.save();

  const gradient = ctx.createLinearGradient(x, y + height, x + width, y);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
  gradient.addColorStop(0.25, 'rgba(255, 255, 255, 0.4)');
  gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.1)');
  gradient.addColorStop(0.75, 'rgba(255, 255, 255, 0.1)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0.3)');

  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, borderRadius);
  ctx.fill();

  if (backgroundColor !== 'transparent') {
    ctx.fillStyle = backgroundColor;
    ctx.beginPath();
    ctx.roundRect(
      x + borderWidth,
      y + borderWidth,
      width - borderWidth * 2,
      height - borderWidth * 2,
      Math.max(0, borderRadius - borderWidth)
    );
    ctx.fill();
  } else {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.roundRect(
      x + borderWidth,
      y + borderWidth,
      width - borderWidth * 2,
      height - borderWidth * 2,
      Math.max(0, borderRadius - borderWidth)
    );
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  }

  ctx.restore();
};

export const waitForFontLoad = async (fontFamily, timeout = 5000) => {
  if (!document.fonts || !document.fonts.ready) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return true;
  }

  try {
    if (document.fonts.load) {
      const descriptors = [
        `400 16px "${fontFamily}"`,
        `500 16px "${fontFamily}"`,
        `700 16px "${fontFamily}"`
      ];
      await Promise.allSettled(
        descriptors.map((descriptor) => document.fonts.load(descriptor))
      );
    }

    await document.fonts.ready;

    const allFonts = [
      ...new Set(Array.from(document.fonts).map((f) => f.family))
    ];

    const fontAvailable =
      allFonts.includes(fontFamily) ||
      allFonts.some((f) => f.startsWith(fontFamily));

    if (fontAvailable) {
      return true;
    }

    const checks = [
      `300 16px "${fontFamily}"`,
      `400 16px "${fontFamily}"`,
      `500 16px "${fontFamily}"`,
      `700 16px "${fontFamily}"`
    ];

    for (const checkString of checks) {
      if (document.fonts.check(checkString)) {
        return true;
      }
    }

    return false;
  } catch (error) {
    return false;
  }
};

export const drawSocialBackground = (ctx, theme, width, height, padding) => {
  ctx.save();

  if (theme === 'space-theme') {
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, '#231E67');
    gradient.addColorStop(1, '#090D3E');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  } else if (theme === 'earth-theme') {
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, '#FCA361');
    gradient.addColorStop(1, '#C93826');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  } else {
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, width, height);
  }

  ctx.restore();
};

export const getSocialBackgroundColor = (theme) => {
  if (theme === 'space-theme') {
    return '#11124A';
  } else if (theme === 'earth-theme') {
    return '#d75636';
  } else {
    return '#f5f5f5';
  }
};

export const preloadFontForCanvas = async (fontFamily) => {
  return new Promise((resolve) => {
    const testCanvas = document.createElement('canvas');
    const testCtx = testCanvas.getContext('2d');

    testCanvas.width = 100;
    testCanvas.height = 50;

    testCtx.font = `16px "${fontFamily}"`;
    testCtx.fillText('Test', 10, 25);

    testCtx.font = `400 16px "${fontFamily}"`;
    testCtx.fillText('Test', 10, 25);

    testCtx.font = `12px "${fontFamily}"`;
    testCtx.fillText('Test', 10, 25);

    testCtx.font = `32px "${fontFamily}"`;
    testCtx.fillText('Test', 10, 25);

    setTimeout(() => resolve(true), 100);
  });
};

export const drawBackground = (ctx, boardingPassData, width, height) => {
  ctx.save();
  ctx.clearRect(0, 0, width, height);

  if (boardingPassData.backgroundClass === 'space-theme') {
    const baseRadialCenterX = 0;
    const baseRadialCenterY = height;
    const baseRadialRadius = Math.max(width, height);

    const baseRadial = ctx.createRadialGradient(
      baseRadialCenterX,
      baseRadialCenterY,
      0,
      baseRadialCenterX,
      baseRadialCenterY,
      baseRadialRadius
    );
    baseRadial.addColorStop(0, '#33257B');
    baseRadial.addColorStop(1, '#00021F');
    ctx.fillStyle = baseRadial;
    ctx.beginPath();
    ctx.roundRect(0, 0, width, height, CANVAS_CONFIG.BORDER_RADIUS);
    ctx.fill();

    const radial2CenterX = width * 0.92;
    const radial2CenterY = height * 1.18;
    const radial2Radius = Math.max(width * 2.33, height * 2.05);

    const spaceRadial2 = ctx.createRadialGradient(
      radial2CenterX,
      radial2CenterY,
      0,
      radial2CenterX,
      radial2CenterY,
      radial2Radius
    );
    spaceRadial2.addColorStop(0.01, '#854FA9');
    spaceRadial2.addColorStop(0.46, '#0A0E4C');
    spaceRadial2.addColorStop(0.78, 'rgba(249, 249, 249, 0)');
    ctx.fillStyle = spaceRadial2;
    ctx.globalCompositeOperation = 'normal';
    ctx.beginPath();
    ctx.roundRect(0, 0, width, height, CANVAS_CONFIG.BORDER_RADIUS);
    ctx.fill();

    const radial3CenterX = width * -0.05;
    const radial3CenterY = height * 0.1;
    const radial3Radius = Math.max(width * 0.82, height * 0.62);

    const spaceRadial3 = ctx.createRadialGradient(
      radial3CenterX,
      radial3CenterY,
      0,
      radial3CenterX,
      radial3CenterY,
      radial3Radius
    );
    spaceRadial3.addColorStop(0.01, '#060640');
    spaceRadial3.addColorStop(0.99, 'rgba(6, 6, 64, 0)');
    ctx.fillStyle = spaceRadial3;
    ctx.globalCompositeOperation = 'normal';
    ctx.beginPath();
    ctx.roundRect(0, 0, width, height, CANVAS_CONFIG.BORDER_RADIUS);
    ctx.fill();

    const radial4CenterX = width * 0.07;
    const radial4CenterY = height * 1.15;
    const radial4Radius = Math.max(width * 0.6, height * 0.68);

    const spaceRadial4 = ctx.createRadialGradient(
      radial4CenterX,
      radial4CenterY,
      0,
      radial4CenterX,
      radial4CenterY,
      radial4Radius
    );
    spaceRadial4.addColorStop(0.01, '#6C0F16');
    spaceRadial4.addColorStop(0.99, 'rgba(108, 15, 22, 0)');
    ctx.fillStyle = spaceRadial4;
    ctx.globalCompositeOperation = 'normal';
    ctx.beginPath();
    ctx.roundRect(0, 0, width, height, CANVAS_CONFIG.BORDER_RADIUS);
    ctx.fill();
  } else if (boardingPassData.backgroundClass === 'earth-theme') {
    const angle = 41;
    const angleRad = ((angle - 90) * Math.PI) / 180;

    const centerX = width / 2;
    const centerY = height / 2;
    const length = Math.max(width, height) * 1.5;

    const x0 = centerX - Math.cos(angleRad) * length;
    const y0 = centerY - Math.sin(angleRad) * length;
    const x1 = centerX + Math.cos(angleRad) * length;
    const y1 = centerY + Math.sin(angleRad) * length;

    const linearGrad = ctx.createLinearGradient(x0, y0, x1, y1);
    linearGrad.addColorStop(0, '#FFC77B');
    linearGrad.addColorStop(1, '#CB3925');
    ctx.fillStyle = linearGrad;
    ctx.beginPath();
    ctx.roundRect(0, 0, width, height, CANVAS_CONFIG.BORDER_RADIUS);
    ctx.fill();

    const radial1CenterX = width * -0.31;
    const radial1CenterY = height * 0.76;
    const radial1Radius = Math.max(width * 0.9, height * 0.76);

    const earthRadial1 = ctx.createRadialGradient(
      radial1CenterX,
      radial1CenterY,
      0,
      radial1CenterX,
      radial1CenterY,
      radial1Radius
    );
    earthRadial1.addColorStop(0.01, 'rgba(254, 189, 115, 1)');
    earthRadial1.addColorStop(0.5, 'rgba(254, 189, 115, 0.3)');
    earthRadial1.addColorStop(0.99, 'rgba(254, 189, 115, 0)');
    ctx.fillStyle = earthRadial1;
    ctx.globalCompositeOperation = 'screen';
    ctx.beginPath();
    ctx.roundRect(0, 0, width, height, CANVAS_CONFIG.BORDER_RADIUS);
    ctx.fill();

    const radial2CenterX = width * 0.99;
    const radial2CenterY = height * 1.17;
    const radial2Radius = Math.max(width * 0.85, height * 0.75);

    const earthRadial2 = ctx.createRadialGradient(
      radial2CenterX,
      radial2CenterY,
      0,
      radial2CenterX,
      radial2CenterY,
      radial2Radius
    );
    earthRadial2.addColorStop(0.01, 'rgba(254, 178, 131, 1)');
    earthRadial2.addColorStop(0.5, 'rgba(254, 178, 131, 0.4)');
    earthRadial2.addColorStop(0.99, 'rgba(254, 178, 131, 0)');
    ctx.fillStyle = earthRadial2;
    ctx.globalCompositeOperation = 'screen';
    ctx.beginPath();
    ctx.roundRect(0, 0, width, height, CANVAS_CONFIG.BORDER_RADIUS);
    ctx.fill();

    const radial3CenterX = width * 0.99;
    const radial3CenterY = height * -0.21;
    const radial3Radius = Math.max(width * 0.75, height * 0.75);

    const earthRadial3 = ctx.createRadialGradient(
      radial3CenterX,
      radial3CenterY,
      0,
      radial3CenterX,
      radial3CenterY,
      radial3Radius
    );
    earthRadial3.addColorStop(0, 'rgba(182, 45, 39, 0.9)');
    earthRadial3.addColorStop(0.4, 'rgba(182, 45, 39, 0.3)');
    earthRadial3.addColorStop(1, 'rgba(182, 45, 39, 0)');
    ctx.fillStyle = earthRadial3;
    ctx.globalCompositeOperation = 'multiply';
    ctx.beginPath();
    ctx.roundRect(0, 0, width, height, CANVAS_CONFIG.BORDER_RADIUS);
    ctx.fill();
  } else {
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(0, 0, width, height, CANVAS_CONFIG.BORDER_RADIUS);
    ctx.fill();
  }

  ctx.globalCompositeOperation = 'source-over';
  ctx.restore();
};

export const drawCutouts = (ctx, width, height, fillColor = null) => {
  const cutoutY = height * CANVAS_CONFIG.CUTOUT_Y_RATIO + 45;
  const cutoutRadius = CANVAS_CONFIG.CUTOUT_RADIUS;

  if (fillColor) {
    ctx.fillStyle = fillColor;

    ctx.beginPath();
    ctx.arc(0, cutoutY, cutoutRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    ctx.arc(width, cutoutY, cutoutRadius, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.globalCompositeOperation = 'destination-out';

    ctx.beginPath();
    ctx.arc(0, cutoutY, cutoutRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    ctx.arc(width, cutoutY, cutoutRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalCompositeOperation = 'source-over';
  }
};

export const drawLogoWithContainer = (
  ctx,
  img,
  x,
  y,
  containerConfig,
  imageConfig,
  boardingPassData
) => {
  const { CONTAINER } = containerConfig;
  const { IMAGE } = imageConfig;

  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.roundRect(x, y, CONTAINER.WIDTH, CONTAINER.HEIGHT, 4);
  ctx.stroke();

  const imageX = x + IMAGE.PADDING.HORIZONTAL;
  const imageY = y + IMAGE.PADDING.VERTICAL;

  let imageWidth, imageHeight;

  if (IMAGE.calculateWidth) {
    imageWidth = (img.width / img.height) * IMAGE.HEIGHT;
    imageHeight = IMAGE.HEIGHT;
  } else {
    imageWidth = IMAGE.WIDTH;
    imageHeight = IMAGE.HEIGHT;
  }

  ctx.drawImage(img, imageX, imageY, imageWidth, imageHeight);
};

const hexToRgba = (hex, alpha) => {
  const cleanHex = hex.startsWith('#') ? hex.slice(1) : hex;
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const fillTextWithSpacing = (ctx, text, x, y, letterSpacing = 0) => {
  if (letterSpacing === 0) {
    ctx.fillText(text, x, y);
    return;
  }

  const characters = text.split('');
  let currentX = x;

  characters.forEach((char) => {
    ctx.fillText(char, currentX, y);
    const charWidth = ctx.measureText(char).width;
    currentX += charWidth + letterSpacing;
  });
};

const measureTextWithSpacing = (ctx, text, letterSpacing = 0) =>
  ctx.measureText(text).width + Math.max(0, text.length - 1) * letterSpacing;

const breakLongWord = (ctx, word, maxWidth, letterSpacing = 0) => {
  const segments = [];
  let current = '';

  for (const char of word) {
    const candidate = current + char;
    if (
      !current ||
      measureTextWithSpacing(ctx, candidate, letterSpacing) <= maxWidth
    ) {
      current = candidate;
    } else {
      segments.push(current);
      current = char;
    }
  }

  if (current) segments.push(current);
  return segments;
};

const wrapText = (ctx, text, maxWidth, letterSpacing = 0) => {
  const words = text.split(' ');
  const lines = [];
  let current = '';

  const pushLine = () => {
    if (current) {
      lines.push(current);
      current = '';
    }
  };

  words.forEach((word) => {
    if (current) {
      const candidate = `${current} ${word}`;
      if (measureTextWithSpacing(ctx, candidate, letterSpacing) <= maxWidth) {
        current = candidate;
        return;
      }
      pushLine();
    }

    if (measureTextWithSpacing(ctx, word, letterSpacing) <= maxWidth) {
      current = word;
    } else {
      const segments = breakLongWord(ctx, word, maxWidth, letterSpacing);
      current = segments.pop() || '';
      lines.push(...segments);
    }
  });

  pushLine();
  return lines;
};

export const drawTextContent = async (
  ctx,
  boardingPassData,
  appContent,
  width,
  height,
  primaryFont,
  textColor,
  iconSources,
  loadImage
) => {
  const textOpacity = 0.5;
  const translucentTextColor = hexToRgba(textColor, textOpacity);

  const config = {
    arrow: {
      x: 185,
      y: 120,
      size: 1,
      color: '#ffffff',
      enabled: true
    },
    dateTimeLabels: {
      fontSize: 10,
      fontFamily: primaryFont,
      fontWeight: 500,
      color: translucentTextColor,
      y: 460,
      x: CANVAS_CONFIG.MARGINS.MAIN,
      lineHeight: 18,
      textTransform: 'uppercase',
      letterSpacing: 0.5
    },
    dateTimeValues: {
      fontSize: 12,
      fontFamily: primaryFont,
      fontWeight: 300,
      color: textColor,
      x: 200,
      lineHeight: 18,
      yOffset: 14,
      marginBottom: 25,
      letterSpacing: -0.24
    },
    flightLabels: {
      fontSize: 12,
      fontFamily: primaryFont,
      color: textColor,
      fontWeight: 400,
      y: 95,
      x: CANVAS_CONFIG.MARGINS.MAIN + 10,
      letterSpacing: -0.24
    },
    flightValues: {
      fontSize: 32,
      fontFamily: primaryFont,
      color: textColor,
      fontWeight: 400,
      y: 135,
      x: CANVAS_CONFIG.MARGINS.MAIN + 10,
      textTransform: 'uppercase',
      letterSpacing: 0,
      arrowGap: 10
    },
    passengerLabels: {
      fontSize: 10,
      fontFamily: primaryFont,
      fontWeight: 500,
      color: translucentTextColor,
      y: 225,
      x: CANVAS_CONFIG.MARGINS.MAIN + 10,
      textTransform: 'uppercase',
      marginBottom: 20,
      letterSpacing: 0.5
    },
    passengerValues: {
      fontSize: 16,
      fontFamily: primaryFont,
      fontWeight: 400,
      color: textColor,
      x: CANVAS_CONFIG.MARGINS.MAIN,
      marginBottom: 25,
      letterSpacing: -0.32
    },
    passengerInfo: {
      iconSize: 16,
      iconSpacing: 6,
      labelToValueSpacing: 10,
      itemSpacing: 68
    }
  };

  const applyTextTransform = (text, transform) => {
    switch (transform) {
      case 'uppercase':
        return text.toUpperCase();
      case 'lowercase':
        return text.toLowerCase();
      case 'capitalize':
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
      case 'none':
      default:
        return text;
    }
  };

  const flightLabelFontFamily = getFontFamilyForWeight(
    config.flightLabels.fontWeight
  );
  ctx.font = `${config.flightLabels.fontSize}px ${flightLabelFontFamily}`;
  ctx.fillStyle = config.flightLabels.color;

  ctx.textAlign = 'left';
  fillTextWithSpacing(
    ctx,
    'Departing From',
    config.flightLabels.x,
    config.flightLabels.y,
    config.flightLabels.letterSpacing
  );

  const arrivingLabel = 'Arriving In';
  const arrivingLabelWidth =
    ctx.measureText(arrivingLabel).width +
    (arrivingLabel.length - 1) * config.flightLabels.letterSpacing;
  fillTextWithSpacing(
    ctx,
    arrivingLabel,
    width - CANVAS_CONFIG.MARGINS.MAIN - (arrivingLabelWidth + 10),
    config.flightLabels.y,
    config.flightLabels.letterSpacing
  );

  const flightValueFontFamily = getFontFamilyForWeight(
    config.flightValues.fontWeight
  );
  ctx.font = `${config.flightValues.fontSize}px ${flightValueFontFamily}`;
  ctx.fillStyle = config.flightValues.color;

  const departingText = applyTextTransform(
    boardingPassData.departingFrom || 'LEVEL 62 BASE',
    config.flightValues.textTransform
  );
  const arrivingText = applyTextTransform(
    boardingPassData.arrivingIn || 'YOUR NEXT ADVENTURE',
    config.flightValues.textTransform
  );

  const arrivingTextWidth = measureTextWithSpacing(
    ctx,
    arrivingText,
    config.flightValues.letterSpacing
  );
  const arrivingX =
    width - CANVAS_CONFIG.MARGINS.MAIN - (arrivingTextWidth + 10);

  const arrowWidth = 12 * config.arrow.size;
  const departingMaxWidth = Math.max(
    width / 2 - config.flightValues.arrowGap - config.flightValues.x,
    arrivingX -
      config.flightValues.x -
      arrowWidth -
      config.flightValues.arrowGap * 2
  );
  const lineHeight = config.flightValues.fontSize;

  ctx.textAlign = 'left';
  const departingLines = wrapText(
    ctx,
    departingText,
    departingMaxWidth,
    config.flightValues.letterSpacing
  );
  departingLines.forEach((line, index) => {
    fillTextWithSpacing(
      ctx,
      line,
      config.flightValues.x,
      config.flightValues.y + index * lineHeight,
      config.flightValues.letterSpacing
    );
  });

  fillTextWithSpacing(
    ctx,
    arrivingText,
    arrivingX,
    config.flightValues.y,
    config.flightValues.letterSpacing
  );

  if (config.arrow.enabled) {
    const widestDepartingLine = departingLines.reduce(
      (widest, line) =>
        Math.max(
          widest,
          measureTextWithSpacing(ctx, line, config.flightValues.letterSpacing)
        ),
      0
    );
    const departingEndX = config.flightValues.x + widestDepartingLine;
    const arrowX = Math.max(
      departingEndX + config.flightValues.arrowGap,
      departingEndX + (arrivingX - departingEndX - arrowWidth) / 2
    );

    drawDestinationArrow(
      ctx,
      arrowX,
      config.arrow.y,
      config.arrow.size,
      config.arrow.color,
      textColor
    );
  }

  ctx.textAlign = 'left';

  if (iconSources) {
    const passengerInfo = {
      patch: boardingPassData.patchName || 'N/A',
      specialty: boardingPassData.coreValue || 'N/A',
      coreValue: boardingPassData.specialty || 'N/A'
    };

    await drawPassengerInfoWithSpacing(
      ctx,
      config,
      passengerInfo,
      iconSources,
      loadImage,
      applyTextTransform
    );
  }

  drawDateTimeSection(ctx, config, boardingPassData, applyTextTransform, width);
};

export const drawPassengerInfoWithSpacing = async (
  ctx,
  config,
  passengerInfo,
  iconSources,
  loadImage,
  applyTextTransform
) => {
  const {
    passengerLabels,
    passengerValues,
    passengerInfo: infoConfig
  } = config;
  const { iconSize, itemSpacing } = infoConfig;

  const icons = {};
  try {
    if (iconSources.specialty) {
      icons.specialty = await loadImage(iconSources.specialty);
    }
    if (iconSources.coreValue) {
      icons.coreValue = await loadImage(iconSources.coreValue);
    }
  } catch (error) {
    // Icons failed to load
  }

  const items = [
    {
      label: 'Specialty',
      value: passengerInfo.specialty,
      icon: icons.specialty
    },
    {
      label: 'Core Value',
      value: passengerInfo.coreValue,
      icon: icons.coreValue
    }
  ];

  let currentY = passengerLabels.y;

  for (const item of items) {
    if (!item.value || item.value === 'N/A') continue;

    let iconDrawn = false;
    if (item.icon) {
      try {
        const iconX = passengerLabels.x;
        const iconY = currentY - iconSize / 2 + 3;

        const tempCanvas = document.createElement('canvas');
        const iconDisplaySize = iconSize;
        const iconBufferSize = iconDisplaySize * 2;

        tempCanvas.width = iconBufferSize;
        tempCanvas.height = iconBufferSize;
        const tempCtx = tempCanvas.getContext('2d');

        tempCtx.imageSmoothingEnabled = true;
        tempCtx.imageSmoothingQuality = 'high';

        tempCtx.drawImage(item.icon, 0, 0, iconBufferSize, iconBufferSize);

        tempCtx.globalCompositeOperation = 'source-in';
        tempCtx.fillStyle = passengerLabels.color;
        tempCtx.fillRect(0, 0, iconBufferSize, iconBufferSize);

        ctx.save();
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
          tempCanvas,
          iconX,
          iconY,
          iconDisplaySize,
          iconDisplaySize
        );

        ctx.restore();

        iconDrawn = true;
      } catch (error) {
        // Icon drawing failed
      }
    }

    if (!iconDrawn) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(
        passengerLabels.x + iconSize / 2,
        currentY - iconSize / 2 + 3,
        iconSize / 4,
        0,
        2 * Math.PI
      );
      ctx.fillStyle = passengerLabels.color;
      ctx.fill();
      ctx.restore();
    }

    const textX = passengerLabels.x + iconSize + 8;

    const labelFontFamily = getFontFamilyForWeight(passengerLabels.fontWeight);
    ctx.font = `${passengerLabels.fontSize}px ${labelFontFamily}`;
    ctx.fillStyle = passengerLabels.color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    const labelText = applyTextTransform(
      item.label,
      passengerLabels.textTransform
    );
    if (passengerLabels.letterSpacing) {
      fillTextWithSpacing(
        ctx,
        labelText,
        textX,
        currentY + 3,
        passengerLabels.letterSpacing
      );
    } else {
      ctx.fillText(labelText, textX, currentY);
    }

    const valueFontFamily = getFontFamilyForWeight(passengerValues.fontWeight);
    ctx.font = `${passengerValues.fontSize}px ${valueFontFamily}`;
    ctx.fillStyle = passengerValues.color;

    const valueText = item.value;
    const valueY = currentY + 30;

    if (passengerValues.letterSpacing) {
      fillTextWithSpacing(
        ctx,
        valueText,
        26,
        valueY,
        passengerValues.letterSpacing
      );
    } else {
      ctx.fillText(valueText, 26, valueY);
    }

    currentY += itemSpacing;
  }
};

export const drawThemedAccents = async (
  ctx,
  themedAssets,
  width,
  height,
  theme
) => {
  ctx.save();

  const BACKGROUND_ACCENT =
    theme === 'space-theme'
      ? CANVAS_CONFIG.LOGOS.BACKGROUND_ACCENT_SPACE
      : CANVAS_CONFIG.LOGOS.BACKGROUND_ACCENT_EARTH;

  const { SPACEFORCE_ACCENT } = CANVAS_CONFIG.LOGOS;

  try {
    if (themedAssets.backgroundImage) {
      const bgImg = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = themedAssets.backgroundImage;
      });

      ctx.globalAlpha = BACKGROUND_ACCENT.OPACITY;
      ctx.drawImage(
        bgImg,
        BACKGROUND_ACCENT.X_POSITION,
        BACKGROUND_ACCENT.Y_POSITION,
        BACKGROUND_ACCENT.WIDTH,
        BACKGROUND_ACCENT.HEIGHT
      );
      ctx.globalAlpha = 1.0;
    }

    if (themedAssets.spaceforceAccent) {
      const logoImg = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = themedAssets.spaceforceAccent;
      });

      ctx.globalAlpha = SPACEFORCE_ACCENT.OPACITY;
      ctx.drawImage(
        logoImg,
        SPACEFORCE_ACCENT.X_POSITION,
        SPACEFORCE_ACCENT.Y_POSITION,
        SPACEFORCE_ACCENT.WIDTH,
        SPACEFORCE_ACCENT.HEIGHT
      );
      ctx.globalAlpha = 1.0;
    }
  } catch (error) {
    // Themed accents failed to load
  } finally {
    ctx.globalAlpha = 1.0;
    ctx.globalCompositeOperation = 'source-over';
    ctx.restore();
  }
};

export const drawPatch = async (
  ctx,
  boardingPassData,
  loadPatchImage,
  width,
  height
) => {
  const patchConfig = {
    width: 158,
    height: 190,
    padding: {
      horizontal: 18,
      vertical: 21
    },
    position: {
      x: width - 175 - CANVAS_CONFIG.MARGINS.MAIN,
      y: 180
    }
  };

  const containerX = patchConfig.position.x;
  const containerY = patchConfig.position.y;
  const containerWidth = patchConfig.width;
  const containerHeight = patchConfig.height;

  const centerX = containerX + containerWidth / 2;
  const centerY = containerY + containerHeight / 2;
  const radiusX = containerWidth / 2;
  const radiusY = containerHeight / 2;

  ctx.save();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
  ctx.beginPath();
  ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  const patchX = containerX + patchConfig.padding.horizontal;
  const patchY = containerY + patchConfig.padding.vertical;
  const patchWidth = 109;
  const patchHeight = 145;

  const patchCenterX = patchX + patchWidth / 2;
  const patchCenterY = patchY + patchHeight / 2;
  const textColor =
    boardingPassData.backgroundClass === 'space-theme' ? '#ffffff' : '#000000';

  if (boardingPassData.patchImage) {
    try {
      const patchImg = await loadPatchImage(
        boardingPassData.patchImage,
        boardingPassData.patchFile
      );
      const offsetY = getPatchCanvasOffsetY(
        boardingPassData.patchFile,
        boardingPassData.patchImage
      );
      ctx.drawImage(
        patchImg,
        patchX + 7,
        patchY + offsetY,
        patchWidth,
        patchHeight
      );
    } catch (error) {
      drawFallbackPatch(
        ctx,
        patchCenterX,
        patchCenterY,
        Math.min(patchWidth, patchHeight),
        textColor
      );
    }
  } else {
    drawFallbackPatch(
      ctx,
      patchCenterX,
      patchCenterY,
      Math.min(patchWidth, patchHeight),
      textColor
    );
  }
};

export const drawDestinationArrow = (
  ctx,
  x,
  y,
  size = 1,
  color = '',
  textColor
) => {
  ctx.save();
  ctx.fillStyle = textColor;

  const scaledWidth = 12 * size;
  const scaledHeight = 8 * size;

  ctx.beginPath();
  ctx.moveTo(x + scaledWidth, y + scaledHeight * 0.5);
  ctx.lineTo(x + 0, y + 0);
  ctx.lineTo(x + 2.39647 * size, y + scaledHeight * 0.5);
  ctx.lineTo(x + 0.00153732 * size, y + scaledHeight);
  ctx.lineTo(x + scaledWidth, y + scaledHeight * 0.5);
  ctx.closePath();

  ctx.fill();
  ctx.restore();
};

export const drawDateTimeSection = (
  ctx,
  config,
  boardingPassData,
  applyTextTransform,
  width
) => {
  let currentY = config.dateTimeLabels.y;
  const rightX = width - CANVAS_CONFIG.MARGINS.MAIN;

  const dateLabelFontFamily = getFontFamilyForWeight(
    config.dateTimeLabels.fontWeight || 400
  );
  ctx.font = `${config.dateTimeLabels.fontSize}px ${dateLabelFontFamily}`;
  ctx.fillStyle = config.dateTimeLabels.color;

  const dateLabel = applyTextTransform(
    'Date',
    config.dateTimeLabels.textTransform
  );
  const dateLabelWidth =
    ctx.measureText(dateLabel).width +
    (dateLabel.length - 1) * config.dateTimeLabels.letterSpacing;
  fillTextWithSpacing(
    ctx,
    dateLabel,
    rightX - dateLabelWidth,
    currentY,
    config.dateTimeLabels.letterSpacing
  );

  const dateValueY = currentY + config.dateTimeValues.yOffset;
  const dateValueFontFamily = getFontFamilyForWeight(
    config.dateTimeValues.fontWeight || 400
  );
  ctx.font = `${config.dateTimeValues.fontSize}px ${dateValueFontFamily}`;
  ctx.fillStyle = config.dateTimeValues.color;

  const dateValue = boardingPassData.date || new Date().toLocaleDateString();
  const dateValueWidth =
    ctx.measureText(dateValue).width +
    (dateValue.length - 1) * config.dateTimeValues.letterSpacing;
  fillTextWithSpacing(
    ctx,
    dateValue,
    rightX - dateValueWidth,
    dateValueY,
    config.dateTimeValues.letterSpacing
  );

  currentY = dateValueY + config.dateTimeValues.marginBottom;

  const timeLabelFontFamily = getFontFamilyForWeight(
    config.dateTimeLabels.fontWeight || 400
  );
  ctx.font = `${config.dateTimeLabels.fontSize}px ${timeLabelFontFamily}`;
  ctx.fillStyle = config.dateTimeLabels.color;

  const timeLabel = applyTextTransform(
    'Boarding Time',
    config.dateTimeLabels.textTransform
  );
  const timeLabelWidth =
    ctx.measureText(timeLabel).width +
    (timeLabel.length - 1) * config.dateTimeLabels.letterSpacing;
  fillTextWithSpacing(
    ctx,
    timeLabel,
    rightX - timeLabelWidth,
    currentY,
    config.dateTimeLabels.letterSpacing
  );

  const timeValueY = currentY + config.dateTimeValues.yOffset;
  const timeValueFontFamily = getFontFamilyForWeight(
    config.dateTimeValues.fontWeight || 400
  );
  ctx.font = `${config.dateTimeValues.fontSize}px ${timeValueFontFamily}`;
  ctx.fillStyle = config.dateTimeValues.color;

  const timeValue =
    boardingPassData.time ||
    new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  const timeValueWidth =
    ctx.measureText(timeValue).width +
    (timeValue.length - 1) * config.dateTimeValues.letterSpacing;
  fillTextWithSpacing(
    ctx,
    timeValue,
    rightX - timeValueWidth,
    timeValueY,
    config.dateTimeValues.letterSpacing
  );

  ctx.textAlign = 'left';
};

const drawFallbackPatch = (ctx, centerX, centerY, size, textColor) => {
  ctx.fillStyle = textColor;
  ctx.font = 'bold 14px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('PATCH', centerX, centerY - 5);
  ctx.font = '10px Arial';
  ctx.fillText('ACHIEVEMENT', centerX, centerY + 8);
  ctx.textAlign = 'left';
};

export { CANVAS_CONFIG };
